import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { PermissionButton } from 'src/routes/components/permission-button';
import {
  ProviderAuditLockState,
  getProviderAuditLocks,
  unlockProviderAudit,
} from 'src/pages/admin/services/xbitRelayProviderAuditService';

import {
  XBIT_RELAY_PROVIDER_AUDIT_PERMISSION_RESOURCE,
  XBIT_RELAY_PROVIDER_AUDIT_UNLOCK_ACTION,
} from './constants';
import { errorMessage, formatUnixMs } from './helpers';

type LockStateDialogProps = {
  userId: string;
  provider: string;
  /**
   * Seed for callers whose row already carries the lock state (the Locks tab), so opening the
   * dialog costs no request. Callers without it (the Mismatches tab) fall back to fetching.
   * The post-unlock confirmation re-read always happens either way.
   */
  initialState?: ProviderAuditLockState;
  onClose(): void;
};

const LockRow = ({ state }: { state: ProviderAuditLockState }) => {
  const t = useTranslate();
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {state.provider}
        </Typography>
        <Chip
          size="small"
          label={
            state.locked
              ? t('module.xbitRelayProviderAudit.locked')
              : t('module.xbitRelayProviderAudit.unlocked')
          }
          color={state.locked ? 'error' : 'success'}
          variant={state.locked ? 'filled' : 'outlined'}
        />
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('module.xbitRelayProviderAudit.lockedAt')}: {formatUnixMs(state.lockedAtUnixMs)}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
        {t('module.xbitRelayProviderAudit.lockReason')}: {state.reason || '-'}
      </Typography>
    </Stack>
  );
};

export const LockStateDialog = ({
  userId,
  provider,
  initialState,
  onClose,
}: LockStateDialogProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [states, setStates] = useState<ProviderAuditLockState[]>(
    initialState ? [initialState] : []
  );
  const [loading, setLoading] = useState(!initialState);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');

  // Always scoped to one (user, provider): an unfiltered call returns every lock row.
  const loadLocks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getProviderAuditLocks({ userId, provider });
      setStates(data?.states ?? []);
      return data?.states ?? [];
    } catch (error) {
      notify(errorMessage(error, t('module.xbitRelayProviderAudit.loadLocksFailed')), {
        type: 'error',
      });
      setStates([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId, provider, notify, t]);

  useEffect(() => {
    if (initialState) return;
    loadLocks();
  }, [initialState, loadLocks]);

  const isLocked = states.some((state) => state.locked);

  const handleUnlock = async () => {
    setSubmitting(true);
    try {
      const { data } = await unlockProviderAudit({ userId, provider, reason: reason.trim() });

      // Business failures arrive as HTTP 200 with success: false.
      if (!data?.success) {
        notify(data?.message || t('module.xbitRelayProviderAudit.unlockRejected'), {
          type: 'error',
        });
        return;
      }

      // The unlock response is not proof — re-read the lock state to confirm.
      const fresh = await loadLocks();
      if (fresh.some((state) => state.locked)) {
        notify('module.xbitRelayProviderAudit.unlockStillLocked', { type: 'warning' });
        return;
      }

      setReason('');
      notify('module.xbitRelayProviderAudit.unlockSuccess', { type: 'success' });
      // Keeps the Locks tab in step; a no-op refetch for the Mismatches tab.
      refresh();
    } catch (error) {
      notify(errorMessage(error, t('module.xbitRelayProviderAudit.unlockRejected')), {
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelayProviderAudit.lockDialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              <b>{t('module.xbitRelayProviderAudit.userId')}:</b> {userId}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayProviderAudit.provider')}:</b> {provider}
            </Typography>
          </Stack>

          <Divider />

          {loading && <CircularProgress size={20} />}

          {!loading && states.length === 0 && (
            <Alert severity="info">{t('module.xbitRelayProviderAudit.noLockState')}</Alert>
          )}

          {!loading &&
            states.map((state) => (
              <LockRow key={`${state.userId}:${state.provider}`} state={state} />
            ))}

          {!loading && isLocked && (
            <PermissionButton
              resource={XBIT_RELAY_PROVIDER_AUDIT_PERMISSION_RESOURCE}
              action={XBIT_RELAY_PROVIDER_AUDIT_UNLOCK_ACTION}
            >
              <TextField
                label={t('module.xbitRelayProviderAudit.unlockReason')}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                size="small"
                fullWidth
                required
                multiline
                minRows={2}
                helperText={t('module.xbitRelayProviderAudit.reasonHelp')}
              />
            </PermissionButton>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={submitting}>
          {t('actions.close')}
        </Button>
        {isLocked && (
          <PermissionButton
            resource={XBIT_RELAY_PROVIDER_AUDIT_PERMISSION_RESOURCE}
            action={XBIT_RELAY_PROVIDER_AUDIT_UNLOCK_ACTION}
          >
            <Button
              variant="contained"
              color="warning"
              onClick={handleUnlock}
              disabled={submitting || loading || !reason.trim()}
              startIcon={submitting ? <CircularProgress size={16} /> : null}
            >
              {t('module.xbitRelayProviderAudit.unlock')}
            </Button>
          </PermissionButton>
        )}
      </DialogActions>
    </>
  );
};
